import { Button } from '@/components/ui/button';
import { ArrowsIn, ArrowsOut } from '@phosphor-icons/react';
import { type ReactNode, useEffect, useState } from 'react';

/**
 * Wrapper que permite expandir su contenido a fullscreen.
 *
 * Uso:
 *   <TablaExpandible titulo="Mi tabla">
 *     <table>...</table>
 *   </TablaExpandible>
 *
 * En estado normal, ocupa el espacio del parent. En estado expandido,
 * fixed sobre toda la viewport (z-50) con overlay oscuro y botón
 * "Colapsar" en esquina superior derecha. ESC también colapsa.
 */
export function TablaExpandible({
  titulo,
  children,
  acciones,
}: {
  titulo: string;
  children: ReactNode;
  /** Botones extra opcionales (ej. "Expandir todo / Colapsar") que se muestran junto al toggle. */
  acciones?: ReactNode;
}) {
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    if (!expandido) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandido(false);
    };
    document.addEventListener('keydown', onKey);
    // Bloquear scroll del body mientras está expandido
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expandido]);

  if (expandido) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-zinc-50">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
          <h2 className="text-sm font-semibold">{titulo}</h2>
          <div className="flex items-center gap-2">
            {acciones}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpandido(false)}
              className="h-7 gap-1 text-xs"
              title="Colapsar (ESC)"
            >
              <ArrowsIn className="h-3 w-3" weight="bold" />
              Colapsar
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">{children}</div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute right-2 top-2 z-10 flex items-center gap-2">
        {acciones}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpandido(true)}
          className="h-7 gap-1 text-xs"
          title="Expandir a pantalla completa"
        >
          <ArrowsOut className="h-3 w-3" weight="bold" />
          Expandir
        </Button>
      </div>
      {children}
    </div>
  );
}
